import { createSelector } from 'reselect';
import { StateInterface } from 'Interfaces/StateInterface';
import {
  AdminGroup, AdminGroups, AdminUser, AdminUsers, NavItem,
} from 'Interfaces/Admin';

/**
 *  Replacements for the methods that used to live on GroupCollection and
 *  UserCollection (WebContent-src/admin/collections). Kept as pure functions so
 *  they can be unit tested and reused outside of connected components.
 */

// ------------------------------------------------------------ pure helpers

/**
 *  Every descendant of `groupId` — children, grandchildren and beyond.
 *  (The legacy name said "subgroups" but it always meant the whole subtree.)
 *
 *  When `active` is given, only groups with that active flag are walked and
 *  returned. The legacy implementation dropped the flag from the third level
 *  down, so deactivated deep subgroups leaked into counts; that is fixed here.
 */
export const collectDescendants = (
  groups: AdminGroups, groupId: number, active?: boolean,
): AdminGroups => {
  const byParent = new Map<number, AdminGroups>();
  groups.forEach((group) => {
    if (active !== undefined && group.active !== active) {
      return;
    }
    const siblings = byParent.get(group.parentId as number);
    if (siblings) {
      siblings.push(group);
    } else {
      byParent.set(group.parentId as number, [group]);
    }
  });

  const descendants: AdminGroups = [];
  // Seeded with the origin so a cycle cannot walk back onto it.
  const seen = new Set<number>([groupId]);
  const walk = (parentId: number) => {
    (byParent.get(parentId) || []).forEach((child) => {
      // Guard against a cycle in the parent chain rather than recursing forever.
      if (seen.has(child.id)) {
        return;
      }
      seen.add(child.id);
      descendants.push(child);
      walk(child.id);
    });
  };
  walk(groupId);
  return descendants;
};

/**
 *  Walks up the parent chain until it finds a group flagged as an organization.
 *  Returns the group itself when it is one, or undefined when the chain ends.
 */
export const findParentOrganization = (
  groups: AdminGroups, groupId: number | null,
): AdminGroup | undefined => {
  const seen = new Set<number>();
  let current = groups.find((g) => g.id === groupId);
  while (current && !seen.has(current.id)) {
    if (current.organization) {
      return current;
    }
    seen.add(current.id);
    current = groups.find((g) => g.id === current?.parentId);
  }
  return undefined;
};

/** True when `groupId` sits anywhere beneath (or is) the superuser group. */
export const isSuperUserChild = (groups: AdminGroups, groupId: number | null): boolean => {
  const seen = new Set<number>();
  let current = groups.find((g) => g.id === groupId);
  while (current && !seen.has(current.id)) {
    if (current.superuser_group) {
      return true;
    }
    seen.add(current.id);
    current = groups.find((g) => g.id === current?.parentId);
  }
  return false;
};

/**
 *  Users of a group, optionally including every subgroup beneath it and
 *  optionally filtered by active flag.
 */
export const collectGroupUsers = (
  users: AdminUsers,
  groups: AdminGroups,
  groupId: number,
  includeSubgroups: boolean,
  active?: boolean,
): AdminUsers => {
  const matches = (user: AdminUser, id: number) =>
    user.groupId === id && (active === undefined || user.active === active);

  if (!includeSubgroups) {
    return users.filter((user) => matches(user, groupId));
  }

  const ids = new Set<number>([groupId]);
  collectDescendants(groups, groupId, active).forEach((g) => ids.add(g.id));
  return users.filter(
    (user) => ids.has(user.groupId) && (active === undefined || user.active === active),
  );
};

/** Builds the group/user tree the column navigation walks. */
export const buildNavTree = (groups: AdminGroups, users: AdminUsers): NavItem[] => {
  const items: NavItem[] = [];

  groups.forEach((group) => {
    if (group.active) {
      items.push({
        id: group.id,
        name: group.name,
        type: 'group',
        parentId: group.parentId || 0,
        active: group.active,
        children: [],
      });
    }
  });

  users.forEach((user) => {
    if (user.active) {
      items.push({
        id: user.id,
        name: user.userName,
        type: 'user',
        // A user's parent is always its group.
        parentId: user.groupId,
        active: user.active,
        children: [],
      });
    }
  });

  // Only groups can be parents, so index those alone.
  const groupsById = new Map<number, NavItem>();
  items.forEach((item) => {
    if (item.type === 'group') {
      groupsById.set(item.id, item);
    }
  });
  items.forEach((item) => {
    if (item.parentId !== 0) {
      groupsById.get(item.parentId)?.children.push(item);
    }
  });

  return items;
};

// -------------------------------------------------------------- selectors

export const adminGroupsSelector = (state: StateInterface): AdminGroups =>
  state.admin.groups.collection;

export const adminUsersSelector = (state: StateInterface): AdminUsers =>
  state.admin.users.collection;

export const adminAccountSelector = (state: StateInterface) => state.admin.account;

export const adminUiSelector = (state: StateInterface) => state.admin.ui;

export const activeAdminGroupsSelector = createSelector(
  adminGroupsSelector,
  (groups) => groups.filter((g) => g.active),
);

export const navItemsSelector = createSelector(
  adminGroupsSelector,
  adminUsersSelector,
  (groups, users) => buildNavTree(groups, users),
);

/** Top-level items for the left-hand list: what the current user may see. */
export const navRootsSelector = createSelector(
  navItemsSelector,
  adminGroupsSelector,
  adminAccountSelector,
  (items, groups, account) => {
    const currentGroup = groups.find((g) => g.id === account.groupId);
    if (currentGroup?.organization) {
      const own = items.find((i) => i.type === 'group' && i.id === account.groupId);
      if (own && own.parentId !== 0) {
        return items.filter((i) => i.parentId === own.id);
      }
    }
    return items.filter((i) => i.parentId === 0);
  },
);
